import styles from './styles.module.scss';
import { useLocales } from '@hooks/helpers/useLocales';
import Address from '@components/address';
import Input from '@components/common/input';
import Select from '@components/common/select';
import FormField from '@components/form_field';
import { mapOccupationsToOptions } from '@utils/index';
import DatePicker from '@components/date_picker';
import { useFieldArray, useFormContext } from 'react-hook-form';
import Checkbox from '@components/common/checkbox';
import Typography from '@components/common/typography';
import Textarea from '@components/common/textarea';
import { useEffect } from 'react';
import { Country } from 'country-state-city';
import Button from '@components/common/button';

const reasons = [
    'I will apply or have applied for a TIN but have not yet received it',
    'My jurisdiction of tax residence does not issue TINs to its residents',
    'Other'
];

const CardHolder = () => {
    const { labelLocale, placeholderLocale, submitLocale } = useLocales();
    const { control, watch } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'tax_info'
    });

    useEffect(() => {
        if (fields.length === 0) {
            append({
                country: '',
                tin: '',
                reason_id: '',
                reason: '',
                no_tin: false
            });
        }
    }, []);

    const watchedInfo = watch('tax_info');

    const getCountryValue = (i: number) => {
        return watchedInfo[i].country;
    };

    const getIsTin = (i: number) => {
        return watchedInfo[i].no_tin;
    };

    const getIsOtherReason = (i: number) => {
        return watchedInfo[i].reason_id === 'Other';
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.content_block}>
                <p className={styles.title}>Personal info</p>
                <div className={styles.form}>
                    <div className={styles.row}>
                        <FormField
                            name="first_name"
                            renderComponent={(props) => (
                                <Input {...props} full label={labelLocale('first_name')} />
                            )}
                        />
                        <FormField
                            name="middle_name"
                            renderComponent={(props) => (
                                <Input
                                    {...props}
                                    full
                                    label={labelLocale('middle_name_optional')}
                                    placeholder={placeholderLocale('enter_middle_name')}
                                />
                            )}
                        />
                    </div>
                    <div className={styles.row}>
                        <FormField
                            name="last_name"
                            renderComponent={(props) => (
                                <Input {...props} full label={labelLocale('last_name')} />
                            )}
                        />
                        <FormField
                            name="alias"
                            renderComponent={(props) => (
                                <Input
                                    placeholder={placeholderLocale('enter_alias')}
                                    {...props}
                                    full
                                    label={labelLocale('alias_optional')}
                                />
                            )}
                        />
                    </div>
                    <div className={styles.row}>
                        <FormField<Date | null>
                            name="birth_date"
                            renderComponent={(props) => (
                                <DatePicker
                                    isBirthday
                                    {...props}
                                    full
                                    label={labelLocale('birth_date')}
                                />
                            )}
                        />
                        <FormField<string | number>
                            name="occupation"
                            renderComponent={(props) => (
                                <Select
                                    items={mapOccupationsToOptions()}
                                    isAllowSearch
                                    {...props}
                                    full
                                    placeholder={placeholderLocale('select_occupation_card')}
                                    label={labelLocale('occupation')}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.content_block}>
                <p className={styles.title}>Contact info</p>
                <div className={styles.form}>
                    <div className={styles.row}>
                        <FormField
                            name="phone_number"
                            renderComponent={(props) => (
                                <Input
                                    {...props}
                                    full
                                    label={labelLocale('phone_number_home_optional')}
                                    placeholder={placeholderLocale('enter_phone_home')}
                                    mask="+1 (999) 999-9999"
                                />
                            )}
                        />
                        <FormField
                            name="cell_phone"
                            renderComponent={(props) => (
                                <Input
                                    {...props}
                                    full
                                    label={labelLocale('cell_phone_number')}
                                    placeholder={placeholderLocale('enter_cell_phone')}
                                    mask="+1 (999) 999-9999"
                                />
                            )}
                        />
                    </div>
                    <div className={styles.row}>
                        <FormField
                            name="email"
                            renderComponent={(props) => (
                                <Input {...props} full label={labelLocale('email')} />
                            )}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.content_block}>
                <p className={styles.title}>Permanent residence address</p>
                <div className={styles.form}>
                    <Address label="permanent_residence" name="permanent_residence" />
                </div>
            </div>
            <div className={styles.content_block}>
                <p className={styles.title}>Tax info (optional)</p>
                {fields.map((item, i) => {
                    return (
                        <div className={styles.form} key={item.id}>
                            {i !== 0 && <div className={styles.divider} />}
                            <div
                                className={styles.row}
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row'
                                }}>
                                <FormField<string | number>
                                    name={`tax_info.${i}.country`}
                                    renderComponent={(props) => (
                                        <Select
                                            items={Country.getAllCountries().map((item) => ({
                                                label: item.name,
                                                value: item.name
                                            }))}
                                            isAllowSearch
                                            {...props}
                                            full
                                            placeholder={placeholderLocale('select_jurisdiction')}
                                            label={labelLocale('jurisdiction_residence')}
                                        />
                                    )}
                                />
                                {i !== 0 && (
                                    <Button
                                        onClick={() => remove(i)}
                                        className={styles.trash}
                                        leftIcon="trash"
                                    />
                                )}
                            </div>
                            <div
                                className={styles.row}
                                style={{ alignItems: 'center', display: 'flex' }}>
                                <FormField
                                    name={`tax_info.${i}.tin`}
                                    renderComponent={(props) => (
                                        <Input
                                            {...props}
                                            full
                                            label={labelLocale(
                                                getCountryValue(i) === 'Canada' ? 'sin' : 'tin'
                                            )}
                                            placeholder={placeholderLocale(
                                                getCountryValue(i) === 'Canada' ? 'sin' : 'tin'
                                            )}
                                        />
                                    )}
                                />
                                <FormField<boolean>
                                    name={`tax_info.${i}.no_tin`}
                                    renderComponent={({ value, onChange }) => {
                                        return (
                                            <div className={styles.no_sin}>
                                                <Checkbox
                                                    checked={value}
                                                    onClick={() => onChange(!value)}
                                                />
                                                <Typography onClick={() => onChange(!value)}>
                                                    {labelLocale(
                                                        getCountryValue(i) === 'Canada'
                                                            ? 'no_sin'
                                                            : 'no_tin'
                                                    )}
                                                </Typography>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                            {getIsTin(i) && (
                                <>
                                    <div className={styles.row}>
                                        <FormField<string | number>
                                            name={`tax_info.${i}.reason_id`}
                                            renderComponent={(props) => (
                                                <Select
                                                    items={reasons.map((item) => ({
                                                        label: item,
                                                        value: item
                                                    }))}
                                                    {...props}
                                                    full
                                                    placeholder={placeholderLocale(
                                                        getCountryValue(i) === 'Canada'
                                                            ? 'select_no_sin_reason'
                                                            : 'select_no_tin_reason'
                                                    )}
                                                    label={labelLocale(
                                                        getCountryValue(i) === 'Canada'
                                                            ? 'no_sin_reason'
                                                            : 'no_tin_reason'
                                                    )}
                                                />
                                            )}
                                        />
                                    </div>
                                    {getIsOtherReason(i) && (
                                        <div className={styles.row}>
                                            <FormField
                                                name={`tax_info.${i}.reason`}
                                                renderComponent={(props) => (
                                                    <Textarea
                                                        {...props}
                                                        full
                                                        placeholder={placeholderLocale(
                                                            'no_sin_tin_reason'
                                                        )}
                                                        label={labelLocale('no_sin_tin_reason')}
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                            {fields.length === i + 1 && (
                                <Button
                                    variant="text-gold"
                                    leftIcon="plus"
                                    size="small"
                                    className={styles.add_button}
                                    onClick={() =>
                                        append({
                                            country: '',
                                            tin: '',
                                            reason_id: '',
                                            reason: '',
                                            no_tin: false
                                        })
                                    }>
                                    {submitLocale('add_jurisdiction')}
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CardHolder;
